// CopilotService.js
// GitHub Device Flow ile GitHub token + Copilot token alma ve kullanım bilgisi servisleri
// NOT: Chat için doğrudan Copilot iç endpointleri yerine OpenAI uyumlu proxy gereklidir.
// CopilotService - Backend (Express) proxy üzerinden GitHub Device Flow + Copilot kullanım/tokenu + opsiyonel chat
// Frontend dev sunucusu package.json 'proxy' alanı ile http://localhost:4000'a yönleniyor.

class CopilotService {
    constructor(config){
        this.backendBase = config?.backendBase || ''; // CRA proxy => boş
        this.proxyBase = (config?.proxyBase || localStorage.getItem('copilot_proxy_base') || 'http://localhost:4141').replace(/\/$/,'');
        this._cachedCopilotToken = null;
        this._cachedCopilotTokenExp = 0; // seconds epoch
    }
    setProxyBase(url){ if(url){ this.proxyBase = url.replace(/\/$/,''); localStorage.setItem('copilot_proxy_base', this.proxyBase); } }

    // ---- Auth / Device Flow ----
    async _parseJsonResponse(res, defaultErrMsg){
        let data = null;
        try { data = await res.json(); } catch(_) { /* yoksay */ }
        if(!res.ok || (data && data.ok === false)){
            const err = (data && (data.error_description || data.error || data.details)) || defaultErrMsg || ('İstek başarısız: '+res.status);
            const e = new Error(err);
            e.payload = data;
            e.status = res.status;
            throw e;
        }
        return data;
    }
    async startDeviceCode(){
        const res = await fetch(this.backendBase + '/api/auth/device-code', { method:'POST', credentials:'include' });
        return this._parseJsonResponse(res,'Device code alınamadı');
    }
    async pollDeviceCode(device_code){
        const res = await fetch(this.backendBase + '/api/auth/poll', { method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body: JSON.stringify({device_code}) });
        // polling de pending durumunu hata olarak fırlatmak istemiyoruz; ok:false & status:pending ise direkt döndür.
        const json = await res.json();
        if(!res.ok){
            const e = new Error(json.error_description || json.error || 'Poll isteği başarısız');
            e.payload = json; e.status = res.status; throw e;
        }
        return json; // pending vs ok üst katmanda yorumlanacak
    }
    async getAuthStatus(){
        const res = await fetch(this.backendBase + '/api/auth/status', { credentials:'include' });
        return this._parseJsonResponse(res,'Auth status alınamadı');
    }
    async logout(){
        await fetch(this.backendBase + '/api/auth/logout', { method:'POST', credentials:'include' });
    }
    async patLogin(token){
        const res = await fetch(this.backendBase + '/api/auth/pat', {
            method:'POST', credentials:'include', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ token })
        });
        return this._parseJsonResponse(res,'PAT girişi başarısız');
    }

    // ---- Copilot Data ----
    async getUsage(){
        const res = await fetch(this.backendBase + '/api/copilot/usage', { credentials:'include' });
        return this._parseJsonResponse(res,'Usage alınamadı');
    }
    async getCopilotToken(){
        const res = await fetch(this.backendBase + '/api/copilot/token', { credentials:'include' });
        const data = await this._parseJsonResponse(res,'Copilot token alınamadı');
        if(data?.token){
            this._cachedCopilotToken = data.token;
            // Try parse exp= from prefix OR use expires_at
            let exp = 0;
            if(data.expires_at){ exp = data.expires_at; }
            else {
                try {
                    const prefix = data.token.split(':')[0];
                    const parts = prefix.split(';');
                    for(const p of parts){ const [k,v] = p.split('='); if(k==='exp'){ const n = parseInt(v,10); if(!isNaN(n)) exp = n; } }
                } catch { /* ignore */ }
            }
            this._cachedCopilotTokenExp = exp || 0;
        }
        return data;
    }
    _validCachedCopilotToken(){ return this._cachedCopilotToken && (!this._cachedCopilotTokenExp || this._cachedCopilotTokenExp > Math.floor(Date.now()/1000)+5); }
    // Proxy modelleri
    async getModels(){
        // Önce backend (aynı sunucu) üstünden dene
        try {
            const resBackend = await fetch(this.backendBase + '/api/v1/models', { credentials:'include' });
            if(resBackend.ok){
                return this._parseJsonResponse(resBackend,'Proxy modelleri alınamadı');
            }
        } catch(_) { /* ignore backend attempt errors */ }
        // Fallback: dış proxyBase (Copilot tokenı real bearer ile gönderelim)
        let bearer = 'dummy';
        try {
            if(!this._validCachedCopilotToken()){
                await this.getCopilotToken();
            }
            if(this._validCachedCopilotToken()) bearer = this._cachedCopilotToken;
        } catch { /* ignore token retrieval errors */ }
        const res = await fetch(this.proxyBase + '/v1/models', { headers:{Authorization:'Bearer '+ bearer, 'accept':'application/json'}, credentials:'include' });
        return this._parseJsonResponse(res,'Proxy modelleri alınamadı');
    }
    async chat({messages, model, temperature=0.2, max_tokens=1024}){
        const body = { model, messages, temperature, max_tokens };
        // Önce backend dene
        try {
            const resBackend = await fetch(this.backendBase + '/api/v1/chat/completions', {
                method:'POST',
                headers:{'Content-Type':'application/json'},
                credentials:'include',
                body: JSON.stringify(body)
            });
            if(resBackend.ok){
                return await resBackend.json();
            }
        } catch(_) { /* ignore */ }
        // Fallback proxy (gerçek bearer kullan)
        let bearer = 'dummy';
        try {
            if(!this._validCachedCopilotToken()) await this.getCopilotToken();
            if(this._validCachedCopilotToken()) bearer = this._cachedCopilotToken;
        } catch { /* ignore */ }
        const res = await fetch(this.proxyBase + '/v1/chat/completions', { method:'POST', headers:{'Content-Type':'application/json', Authorization:'Bearer '+ bearer}, credentials:'include', body: JSON.stringify(body)});
        const data = await res.json();
        if(!res.ok) {
            const err = data?.error?.message || data?.error || ('Chat hatası: '+res.status);
            const e = new Error(err); e.payload=data; e.status=res.status; throw e;
        }
        return data;
    }
    async chatNative({ messages, model='gpt-5', temperature=0, top_p=1, n=1, debug=false }){
        // Yeni backend uç noktası: /api/copilot/chat (stream=false toplu)
        const body = { messages, model, temperature, top_p, n, debug };
        const res = await fetch(this.backendBase + '/api/copilot/chat', {
            method:'POST',
            headers:{'Content-Type':'application/json'},
            credentials:'include',
            body: JSON.stringify(body)
        });
        let data = null;
        try { data = await res.json(); } catch { /* ignore parse */ }
        if(!res.ok){
            const err = (data && (data.error || data.message)) || ('Native chat hatası: '+res.status);
            const e = new Error(err); e.payload=data; e.status=res.status; throw e;
        }
        return data; // { role, content, raw_events? }
    }
    async codexComplete({ prompt, max_tokens=400 }){
        const res = await fetch(this.backendBase + '/api/copilot/codex/completion', {
            method:'POST',
            headers:{'Content-Type':'application/json'},
            credentials:'include',
            body: JSON.stringify({ prompt, max_tokens })
        });
        const data = await res.json().catch(()=>null);
        if(!res.ok){
            const err = (data && (data.message || data.error)) || ('Codex hata: '+res.status);
            const e = new Error(err); e.payload=data; e.status=res.status; throw e;
        }
        return data;
    }
    async codexStream({ prompt, language='plaintext', max_tokens=400, stops, onDelta, onDone, onError }){
        const controller = new AbortController();
        try {
            const res = await fetch(this.backendBase + '/api/copilot/codex/stream', {
                method:'POST',
                headers:{'Content-Type':'application/json','Accept':'text/event-stream'},
                credentials:'include',
                body: JSON.stringify({ prompt, language, max_tokens, stops }),
                signal: controller.signal
            });
            if(!res.ok){
                const txt = await res.text();
                throw new Error('Codex stream bağlantı hatası: '+res.status+' '+txt);
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            const processChunk = (text) => {
                buffer += text;
                // SSE mesajları \n\n ile ayrılıyor
                let sepIndex;
                while((sepIndex = buffer.indexOf('\n\n')) >= 0){
                    const rawEvent = buffer.slice(0, sepIndex).trim();
                    buffer = buffer.slice(sepIndex + 2);
                    if(!rawEvent) continue;
                    // Her satır bir veya daha fazla 'data:' içerebilir; satırlara böl.
                    for(const line of rawEvent.split('\n')){
                        const trimmed = line.trim();
                        if(!trimmed.startsWith('data:')) continue;
                        const payload = trimmed.slice(5).trim();
                        if(!payload) continue;
                        if(payload === '[DONE]') { onDone && onDone(); return; }
                        if(payload.startsWith('{')){
                            try {
                                const obj = JSON.parse(payload);
                                if(obj.error){ onError && onError(new Error(obj.error.message || obj.error)); return; }
                                const choices = obj.choices || [];
                                for(const ch of choices){
                                    let piece = '';
                                    if(typeof ch.text === 'string') piece = ch.text;
                                    else if(ch.delta && typeof ch.delta.text === 'string') piece = ch.delta.text;
                                    else if(typeof ch.completion === 'string') piece = ch.completion;
                                    else if(typeof ch.partial === 'string') piece = ch.partial;
                                    if(piece) onDelta && onDelta(piece);
                                }
                            } catch(e){ /* parse hatası yoksay */ }
                        }
                    }
                }
            };
            const pump = async () => {
                while(true){
                    const { done, value } = await reader.read();
                    if(done){ onDone && onDone(); break; }
                    processChunk(decoder.decode(value, { stream:true }));
                }
            };
            pump().catch(err=>{ if(controller.signal.aborted) return; onError && onError(err); });
        } catch(e){
            if(controller.signal.aborted) return { cancel: () => controller.abort() };
            onError && onError(e);
        }
        return { cancel: () => controller.abort() };
    }
    async getCopilotModelsDirect(){
        // Backend yeni uç: /api/copilot/models (Bearer = ensureCopilotToken backend'de hallediliyor)
        const res = await fetch(this.backendBase + '/api/copilot/models', { credentials:'include' });
        return this._parseJsonResponse(res,'Copilot modelleri alınamadı');
    }
}
export const copilotService = new CopilotService({backendBase: ''});
export default CopilotService;
